import React, { useMemo } from 'react';
import { BufferMonthData, TimeGranularity } from './StrategiesUtils';
import { dynamicBufferLinePlugin } from './BufferLinePlugin';
import { mrpShortageLinePlugin } from './MrpShortageLinePlugin';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import { StrategyChart, DatasetConfig } from './StrategyChart';

interface Props {
  buffer: number;
  bufferData: BufferMonthData[];
  matPrice: number;
  unit: IMassUnitObject;
  onTimeChange: (_time: TimeGranularity) => void;
  materialName: string;
  materialId: string;
}

// Transform buffer data to include calculated fields
interface BufferChartData extends BufferMonthData {
  mrpShortage: number | null;
  expectedDemandStacked: number;
}

export const BufferManagement: React.FC<Props> = ({
  buffer,
  bufferData,
  matPrice,
  unit,
  onTimeChange,
  materialName,
  materialId,
}) => {
  const datasets: DatasetConfig[] = [
    {
      label: 'Storage',
      dataKey: 'storage',
      backgroundColor: '#1E3A8A',
      borderColor: '#1E3A8A',
      type: 'bar',
    },
    {
      label: 'POs',
      dataKey: 'po',
      backgroundColor: '#93C5FD',
      borderColor: '#93C5FD',
      type: 'bar',
    },
    {
      label: 'Expected Demand (MRP + Buffer)',
      dataKey: 'visibleExpectedDemand',
      backgroundColor: '#E5E7EB',
      borderColor: '#E5E7EB',
      type: 'bar',
    },
    {
      label: 'Expected Demand (MRP + Buffer)',
      dataKey: 'expectedDemand',
      backgroundColor: '#E5E7EB',
      borderColor: '#E5E7EB',
      type: 'bar',
      hidden: true,
      hideFromLegend: true,
    },
    {
      label: 'Expected Demand (MRP + Buffer)',
      dataKey: 'visExpectedMinusPO',
      backgroundColor: '#E5E7EB',
      borderColor: '#E5E7EB',
      type: 'bar',
      hidden: true,
      hideFromLegend: true,
    },
    {
      label: 'Expected Demand (MRP + Buffer)',
      dataKey: 'visExpectedMinusStock',
      backgroundColor: '#E5E7EB',
      borderColor: '#E5E7EB',
      type: 'bar',
      hidden: true,
      hideFromLegend: true,
    },
    {
      label: 'MRP (hidden)',
      dataKey: 'buffer',
      borderColor: '#9CA3AF',
      borderDash: [3, 2],
      type: 'line',
      hidden: false,
      hideFromLegend: true,
    },
  ];

  const pluginsFactory = useMemo(
    () => (isQuantity: boolean, matPrice: number) => [dynamicBufferLinePlugin(buffer * (isQuantity ? 1 : matPrice))],
    [buffer],
  );

  return (
    <StrategyChart
      materialId={materialId}
      title={`${materialName} - Buffer stock management`}
      data={bufferData}
      matPrice={matPrice}
      unit={unit}
      onTimeChange={onTimeChange}
      datasets={datasets}
      pluginsFactory={pluginsFactory}
      stacked={true}
      extraParams={{ buffer }}
    />
  );
};
